import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Check, CheckCheck, X, Clock, Pencil } from 'lucide-react'
import { formatRelativeTime, getMessageTypeLabel, type Message, type MessageType } from './use-inbox'

interface MessageBubbleProps {
  message: Message
  leadName: string
  onEditRequest?: (message: Message) => void
}

function StatusIcon({ status }: { status: Message['status'] }) {
  switch (status) {
    case 'sent':
      return <Check className="h-3 w-3 text-muted-foreground" />
    case 'delivered':
      return <CheckCheck className="h-3 w-3 text-muted-foreground" />
    case 'read':
      return <CheckCheck className="h-3 w-3 text-blue-500" />
    case 'failed':
      return <X className="h-3 w-3 text-destructive" />
    case 'pending':
      return <Clock className="h-3 w-3 text-muted-foreground" />
    default:
      return null
  }
}

const TYPE_LABEL_MAP: Record<MessageType, string> = {
  connection_request: 'Connection Request',
  first_dm: 'First DM',
  follow_up_1: 'Follow-up 1',
  follow_up_2: 'Follow-up 2',
  ai_reply: 'AI Reply',
  reply: 'Reply',
  message: 'Message',
}

export function MessageBubble({ message, leadName, onEditRequest }: MessageBubbleProps) {
  const isOutbound = message.direction === 'outbound'

  return (
    <div
      className={cn(
        'flex w-full flex-col gap-1',
        isOutbound ? 'items-end animate-message-in-right' : 'items-start animate-message-in-left',
      )}
    >
      {/* Type label */}
      <span
        className={cn(
          'text-[10px] font-medium text-muted-foreground',
          isOutbound ? 'pr-1' : 'pl-1',
        )}
      >
        {TYPE_LABEL_MAP[message.type] ?? getMessageTypeLabel(message.type)}
        {!isOutbound && ` · ${leadName}`}
      </span>

      {/* Bubble */}
      <div
        className={cn(
          'group relative max-w-[75%] rounded-2xl px-4 py-2.5',
          isOutbound
            ? 'rounded-tr-sm bg-primary text-primary-foreground'
            : 'rounded-tl-sm bg-muted text-foreground',
        )}
      >
        {/* AI badge */}
        {message.is_ai_generated && isOutbound && (
          <span className="mb-1.5 flex items-center gap-1">
            <Badge className="h-4 bg-blue-500 px-1.5 py-0 text-[9px] font-bold text-white hover:bg-blue-500">
              AI
            </Badge>
          </span>
        )}

        {/* Sentiment badge for inbound */}
        {!isOutbound && message.sentiment && (
          <span className="mb-1.5 flex items-center gap-1">
            <Badge className={cn(
              'h-4 px-1.5 py-0 text-[9px] font-bold border-0',
              message.sentiment === 'positive' ? 'bg-emerald-500/12 text-emerald-300' :
              message.sentiment === 'negative' ? 'bg-red-500/12 text-red-300' :
              'bg-[rgba(255,255,255,0.05)] text-[var(--color-silver)]'
            )}>
              {message.sentiment}
            </Badge>
          </span>
        )}

        <p className="whitespace-pre-wrap text-sm leading-relaxed">{message.content}</p>
      </div>

      {/* Footer: timestamp + status + edit */}
      <div
        className={cn(
          'flex items-center gap-1.5',
          isOutbound ? 'flex-row-reverse' : 'flex-row',
        )}
      >
        <span className="text-[10px] text-muted-foreground">
          {formatRelativeTime(message.sent_at)}
        </span>
        {isOutbound && (
          <span className="flex items-center">
            <StatusIcon status={message.status} />
          </span>
        )}
        {isOutbound && message.editable && onEditRequest && (
          <Button
            variant="ghost"
            size="sm"
            className="h-5 gap-1 px-1.5 text-[10px] text-muted-foreground hover:text-foreground"
            onClick={() => onEditRequest(message)}
          >
            <Pencil className="h-2.5 w-2.5" />
            Edit &amp; Resend
          </Button>
        )}
      </div>
    </div>
  )
}
